import { useRouteFullPath } from "@/hooks/use-route-full-path";
import {
  AnyContext,
  AnyRoute,
  functionalUpdate,
  Outlet,
  ResolveFullPath,
  ResolveId,
  ResolveParams,
  RouteConstraints,
  Route as TRoute,
  useMatchRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { PreviewState } from "../data-table/data-table-preview";
import React from "react";
import { Namespaced } from "@/components/custom/table-view/search-namespace";
import { PositionState } from "../data-table/data-table-position";
import { cn, isDefined } from "@/lib/utils";
import {
  DataTableContext,
  PaginationRequest,
  useTable,
  useTableCurrentItemRef,
  useTableGallery,
  useTablePosition,
  useTablePreview,
  useTableQueryResult,
  useTableRefreshCounts,
  WithSetter,
} from "../data-table/data-table-context";
import { ApiError, PaginatedResponseDto } from "@/client/extend";
import { QueryKey, useQuery } from "@tanstack/react-query";
import useBreakpoint from "@/hooks/use-breakpoint";
import {
  TableKeyboardHandlersOptions,
  useTableKeyboardHandlers,
} from "../data-table/data-table-keyboard-navigation";
import { SideBySide } from "./side-by-side";
import { GalleryState } from "../data-table/data-table-gallery";
import { NestedProvider, useNested } from "./nested";
import { client } from "@/client";

const IdContext = React.createContext<string | undefined>(undefined);
const IsRootContext = React.createContext<boolean | undefined>(undefined);
const MarkReplaceContext = React.createContext<(() => void) | undefined>(
  undefined,
);

const useTableAccountSwitch = () => {
  const table = useTable();
  const accountsQuery = client.users.me.asQueryOptions();
  const { data: account } = useQuery(accountsQuery);
  const accountId = account?.accountId;
  const accountIdRef = React.useRef(accountId);
  React.useEffect(() => {
    if (accountId === accountIdRef.current) return;
    accountIdRef.current = accountId;
    table.resetRowSelection(false);
    table.setAllSelected(false);
  }, [accountId]);
};

export const DataTableNavigationProvider = <
  TRow = unknown,
  TArgument extends PaginationRequest = PaginationRequest,
  TQueryFnData extends PaginatedResponseDto<TRow> = PaginatedResponseDto<TRow>,
  TError = ApiError,
  TData extends TQueryFnData = TQueryFnData,
  TQueryKey extends QueryKey = any,
  TParentRoute extends RouteConstraints["TParentRoute"] = AnyRoute,
  TPath extends RouteConstraints["TPath"] = "/",
  TFullPath extends RouteConstraints["TFullPath"] = ResolveFullPath<
    TParentRoute,
    TPath
  >,
  TCustomId extends RouteConstraints["TCustomId"] = string,
  TId extends RouteConstraints["TId"] = ResolveId<
    TParentRoute,
    TCustomId,
    TPath
  >,
  TSearchValidator = undefined,
  TParams = ResolveParams<TPath>,
  TRouterContext = AnyContext,
  TRouteContextFn = AnyContext,
  TBeforeLoadFn = AnyContext,
  TLoaderDeps extends Record<string, any> = {},
  TLoaderFn = undefined,
  TChildren = unknown,
>({
  Route,
  namespaced,
  childIdParameter,
  defaultChildRoute,
  context: Context,
  contextProps,
  defaultGallery,
  onGalleryChange,
  children,
}: {
  Route: TRoute<
    TParentRoute,
    TPath,
    TFullPath,
    TCustomId,
    TId,
    TSearchValidator,
    TParams,
    TRouterContext,
    TRouteContextFn,
    TBeforeLoadFn,
    TLoaderDeps,
    TLoaderFn,
    TChildren
  >;
  namespaced: Namespaced;
  childIdParameter?: string; // example deviceid
  defaultChildRoute?: string;
  context: DataTableContext<
    TRow,
    TArgument,
    TQueryFnData,
    TError,
    TData,
    TQueryKey
  >;
  contextProps?: React.ComponentProps<
    DataTableContext<
      TRow,
      TArgument,
      TQueryFnData,
      TError,
      TData,
      TQueryKey
    >["Provider"]
  >;
  children?: React.ReactNode;
  defaultGallery?: GalleryState;
  onGalleryChange?: (gallery: GalleryState) => void;
}) => {
  const routeFullPath = useRouteFullPath(Route);
  const navigate = useNavigate({ from: routeFullPath as any });
  const _search = Route.useSearch();
  // Tracks only THIS provider's own namespaced search keys (never the full search object). When
  // multiple DataTableNavigationProvider instances share one route (e.g. two tables on the same
  // page), each used to cache a snapshot of the *entire* search and resend it verbatim on every
  // navigate — so whichever provider navigated last would stomp the other's just-applied change
  // with its own stale copy of it. Scoping this ref to owned keys, and merging it onto the
  // router's live search via a function-form `search` below, means a navigate from this provider
  // can never clobber a key it doesn't own.
  const searchRef = React.useRef<any>(
    namespaced.forward(namespaced.extract(_search)),
  );
  React.useEffect(() => {
    searchRef.current = namespaced.forward(namespaced.extract(_search));
  }, [_search, namespaced]);
  // const _params = Route.useParams() as any;
  const _params = useParams({
    from: `${routeFullPath}` as any,
  });
  // paramsRef intentionally lags one render behind _params - it's only read inside click-driven
  // setters below (as the `params` passed to navigate()), where "as of last render" is fine. Do
  // NOT derive render-time state like `position.id` from this ref instead of `_params` directly -
  // that lag is exactly what caused a cross-table selection race (see `id` below).
  React.useEffect(() => {
    paramsRef.current = _params;
  }, [_params]);

  const paramsRef = React.useRef(_params);
  const replaceNextRef = React.useRef(false);
  // Set by `closePreview` (row deleted / filtered away), read by the position self-heal in
  // `Wrapped`, cleared by `setPosition` on the next real selection - see all three for why.
  const skipPositionRestoreRef = React.useRef(false);
  const toRef = React.useRef<string | undefined>(undefined);
  const subRef = React.useRef<string | undefined>(defaultChildRoute);

  const matchRoute = useMatchRoute();
  const computeToSub = React.useCallback(() => {
    let sub: string | undefined = undefined;
    if (childIdParameter) {
      try {
        sub = matchRoute({
          from: `${routeFullPath}/$${childIdParameter}` as any,
          fuzzy: true,
        })?.["**"];
      } catch (ex) {
        // ignore
      }
    }
    if (isDefined(sub)) {
      const to = `${routeFullPath}/$${childIdParameter}/${sub ?? ""}`;
      toRef.current = to;
      subRef.current = sub;
      return { sub, to };
    } else if (childIdParameter) {
      // This provider owns a child route (e.g. Alerts' $alertId) but isn't currently on it —
      // navigating there means going back to the bare list route.
      const to = routeFullPath;
      sub = subRef.current ?? defaultChildRoute;
      toRef.current = to;
      subRef.current = sub;
      return { sub, to };
    } else {
      // This provider has no child route of its own (e.g. the metric-alerts table on a page that
      // also has an Alerts table with its own $alertId child route). Its own navigations must
      // never force the pathname back to the bare list route — doing so would blow away whatever
      // *other* provider's child route happens to be open, dropping its path/id while leaving its
      // search keys (like `position`) untouched. That mismatch is exactly what previously tricked
      // the self-healing effect below into re-selecting the other table's row. Leaving `to`
      // undefined means "stay on the current path", same as setPreview/setGallery already do.
      toRef.current = undefined;
      subRef.current = undefined;
      return { sub: undefined, to: undefined };
    }
  }, [matchRoute, childIdParameter, defaultChildRoute, routeFullPath, subRef]);

  const {
    position: positionIndex,
    preview: _preview,
    gallery: _gallery,
    queryArgument: queryArgumentFromSearch,
  } = React.useMemo(() => {
    const { position, preview, gallery, ...queryArgument } =
      namespaced.extract(_search);
    return { position, preview, gallery, queryArgument };
  }, [namespaced.extract, _search]);

  // Merge queryArgument from contextProps with queryArgument from URL search params
  const queryArgument = React.useMemo(() => {
    return {
      ...queryArgumentFromSearch,
      ...(contextProps?.queryArgument as Partial<TArgument>),
    } as TArgument;
  }, [queryArgumentFromSearch, contextProps?.queryArgument]);

  const gallery = _gallery ?? defaultGallery;

  const setQueryArgument = React.useCallback(
    (a: React.SetStateAction<TArgument>) => {
      const next = functionalUpdate(a, queryArgument as TArgument);
      searchRef.current = {
        ...searchRef.current,
        ...namespaced.forward(next as any),
      };
      const replace = replaceNextRef.current;
      replaceNextRef.current = false;
      // const to = toRef.current;
      const { to } = computeToSub();
      const ownSearch = searchRef.current;
      const params = paramsRef.current;
      navigate({
        to,
        params,
        search: (prev: any) => ({ ...prev, ...ownSearch }),
        replace,
      });
    },
    [queryArgument, navigate],
  );
  // Deriving `id` from `paramsRef` (updated one render behind via an effect) instead of `_params`
  // directly means `position.id` lags `position.index` (which comes straight from a `useMemo` on
  // `_search`, no lag) by one render whenever a navigate changes path params and search together
  // — e.g. leaving/entering the $alertId route. That one-render mismatch window is exactly what
  // repeatedly tricked the self-healing branches below into re-selecting a row we'd just cleared.
  const id = childIdParameter ? _params[childIdParameter] : undefined;
  const lastValidRef = React.useRef<PositionState | undefined>(undefined);
  const position: PositionState = {
    index: positionIndex,
    id,
    lastValid: lastValidRef.current,
  };
  const setPosition = React.useCallback(
    (a: React.SetStateAction<PositionState>) => {
      const next_ = functionalUpdate(a, position);
      const { lastValid, ...next } = next_ ?? {};
      searchRef.current = {
        ...searchRef.current,
        ...namespaced.forward({ position: next.index } as any),
      };
      lastValidRef.current = lastValid;
      const replace = replaceNextRef.current;
      replaceNextRef.current = false;
      // A position carrying both an id and an index is a real selection - a row click, or
      // keyboard navigation - never the self-heal re-asserting a stale route param (that one
      // arrives id-only, because the row it names is gone from the model). Once one lands, the
      // "row was deleted, don't restore" flag set by `closePreview` has served its purpose.
      if (next.id && next.index !== undefined) {
        skipPositionRestoreRef.current = false;
      }
      const { sub } = computeToSub();
      if (childIdParameter && next.id && sub !== undefined) {
        toRef.current = `${routeFullPath}/$${childIdParameter}/${sub}` as any;
        paramsRef.current = { [childIdParameter]: next.id };
      }

      const to = toRef.current;
      const ownSearch = searchRef.current;
      const params = paramsRef.current;
      navigate({
        to,
        search: (prev: any) => ({ ...prev, ...ownSearch }),
        params,
        replace,
      });
    },
    [position, routeFullPath, navigate, matchRoute, childIdParameter],
  );
  const preview: PreviewState = _preview ?? (id !== undefined ? 2 : 0);
  // `closeChild` is the "previewed row is gone" case (deleted, or filtered out of the list),
  // as opposed to the user collapsing the panel with Escape / the close button - which keeps
  // the item addressable, so re-opening shows it again. The entity id lives in the path
  // (`.../devices/$deviceId`), so closing on a row that no longer exists has to leave the
  // child route too, or the dead id sits in the URL (ID-5883). The numeric `position` search
  // key rides along either way, keeping the user's place in the table.
  const applyPreview = React.useCallback(
    (next: PreviewState, closeChild?: boolean) => {
      searchRef.current = {
        ...searchRef.current,
        ...namespaced.forward({ preview: next } as any),
      };
      const ownSearch = searchRef.current;
      const replace = replaceNextRef.current;
      replaceNextRef.current = false;
      let to: string | undefined = undefined;
      let params = paramsRef.current;
      if (closeChild && childIdParameter) {
        const { [childIdParameter]: _closedId, ...rest } = params ?? {};
        params = rest;
        // Point the refs at where we're going - a setter firing before the `_params` effect
        // catches up would otherwise read the stale child path back out.
        paramsRef.current = rest;
        to = toRef.current = routeFullPath;
        // `Wrapped` self-heals the position as soon as the refetched row model lands: it
        // either re-asserts the (now dead) id it still reads off the route params, or derives
        // a fresh one from the numeric position - whichever row shifted into that index.
        // Either puts an id back in the path a beat after we removed it.
        skipPositionRestoreRef.current = true;
      }
      navigate({
        to,
        params,
        search: (prev: any) => ({ ...prev, ...ownSearch }),
        replace,
      } as any);
    },
    [routeFullPath, navigate, childIdParameter, namespaced],
  );
  const setPreview = React.useCallback(
    (a: React.SetStateAction<PreviewState>) =>
      applyPreview(functionalUpdate(a, preview)),
    [preview, applyPreview],
  );
  const closePreview = React.useCallback(
    () => applyPreview(0, true),
    [applyPreview],
  );
  const setGallery = React.useCallback(
    (a: React.SetStateAction<GalleryState>) => {
      const next = functionalUpdate(a, gallery);
      searchRef.current = {
        ...searchRef.current,
        ...namespaced.forward({ gallery: next } as any),
      };
      // const to = toRef.current;
      const ownSearch = searchRef.current;
      const params = paramsRef.current;
      const replace = replaceNextRef.current;
      replaceNextRef.current = false;
      // console.log("setPreview", { /*to,*/ params, ownSearch, replace });
      if (onGalleryChange) {
        onGalleryChange(next);
      }
      navigate({
        /*to,*/ params,
        search: (prev: any) => ({ ...prev, ...ownSearch }),
        replace,
      } as any);
    },
    [gallery, routeFullPath, navigate],
  );
  const markReplace = React.useCallback(() => {
    replaceNextRef.current = true;
  }, [replaceNextRef]);

  const isRootRef = React.useRef<boolean | undefined>(undefined);
  try {
    isRootRef.current = !!matchRoute({
      to: `${routeFullPath}` as any,
      fuzzy: false,
    });
  } catch (ex) {
    //
  }
  const isRoot = isRootRef.current;
  return (
    <Context.Provider
      queryArgumentState={
        [queryArgument, setQueryArgument] as WithSetter<TArgument>
      }
      positionState={[position, setPosition]}
      previewState={[preview, setPreview]}
      onPreviewClose={closePreview}
      galleryState={[gallery, setGallery]}
      {...contextProps}
    >
      <IsRootContext.Provider value={isRoot}>
        <MarkReplaceContext.Provider value={markReplace}>
          <IdContext.Provider value={id}>
            <Wrapped
              childIdParameter={childIdParameter}
              skipPositionRestoreRef={skipPositionRestoreRef}
            >
              {children}
            </Wrapped>
          </IdContext.Provider>
        </MarkReplaceContext.Provider>
      </IsRootContext.Provider>
    </Context.Provider>
  );
};

function Wrapped({
  children,
  childIdParameter,
  skipPositionRestoreRef,
}: {
  children?: React.ReactNode;
  childIdParameter?: string;
  skipPositionRestoreRef?: React.MutableRefObject<boolean>;
}) {
  const id = React.useContext(IdContext);
  const markReplace = React.useContext(MarkReplaceContext);
  const table = useTable();
  const { data, isLoading } = useTableQueryResult();
  const refreshCounts = useTableRefreshCounts();
  const [position, _setPosition] = useTablePosition();
  const positionAttemptRef = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    if (isLoading) return;
    // The previewed row was deleted and `closePreview` took its id out of the path. Every
    // branch below would put one back: `id`/`position.id` still carry the dead id (they come
    // from route params, which lag a render behind the navigate), and the id-less branch
    // re-derives one from the numeric position - i.e. whichever row shifted into that index.
    // Nothing is selected any more, so there is nothing to heal; `setPosition` lifts this as
    // soon as the user picks a real row.
    if (skipPositionRestoreRef?.current) return;
    if (id !== undefined && position.id === undefined) {
      // console.log("on filter");
      try {
        const row = table.getRow(id, false);
        const rowPositionIndex = row.getPositionIndex();
        if (rowPositionIndex !== position.index) {
          // console.log(`position change ${position.index} => ${rowPositionIndex}`,);
          if (markReplace) {
            markReplace();
          }
          row.setIsPosition(true);
          return;
        }
      } catch (ex) {
        return;
      }
    } else if (position.id !== undefined && position.index !== undefined) {
      try {
        const row = table.getRow(position.id, true);
        const rowPositionIndex = row.getPositionIndex();
        if (rowPositionIndex !== position.index) {
          // console.log(`position change ${position.index} => ${rowPositionIndex}`,);
          if (markReplace) {
            markReplace();
          }
          row.setIsPosition(true);
          return;
        }
      } catch (ex) {
        if (table.shouldContainPosition(position)) {
          // console.log(`position change ${position.index} => ${undefined}`);
          if (markReplace) {
            markReplace();
          }
          table.setPosition({
            id: position.id,
            lastValid:
              position.index !== undefined ? position : position?.lastValid,
          });
          refreshCounts();
          return;
        }
      }
    } else if (position.id !== undefined) {
      // && position.lastValid?.index !== undefined
      try {
        const row = table.getRow(position.id, false);
        const rowPositionIndex = row.getPositionIndex();
        if (
          rowPositionIndex !== (position.index ?? position.lastValid?.index) // #TODO: this is a guard rail to not infinitely fetch - for some reason table.getRow(id) does not fail here, but fails when index exists (on condition above, occurs when going back with browser after delete) but this does not allow re-adding of item to list
        ) {
          // console.log(`position change ${position.index ?? position.lastValid?.index} => ${rowPositionIndex}`,);
          if (markReplace) {
            markReplace();
          }
          row.setIsPosition(true);
          return;
        }
      } catch (ex) {
        // console.log(`did not find position for ${position.id}${position?.lastValid?.index !== undefined ? ` (${position?.lastValid?.index})` : ''}`);
      }
    } else if (childIdParameter && position.index !== undefined) {
      // console.log("position change without id", position.index, position);
      if (markReplace) {
        markReplace();
      }
      if (positionAttemptRef.current === position.index) {
        // console.log("position change without id failed", position.index);
        return;
      }
      positionAttemptRef.current = position.index;
      table.setPositionIndex(position.index);
    }
  }, [table, data, position, id, isLoading, skipPositionRestoreRef]);
  useTableAccountSwitch();
  return children;
}

export function SideBySideOutlet({
  children,
  keyboardOptions,
}: {
  children?: React.ReactNode;
  keyboardOptions?: TableKeyboardHandlersOptions & {
    disableUpDownOnFullScreen?: boolean;
  };
}) {
  const isRoot = React.useContext(IsRootContext);
  const table = useTable();
  const [preview, setPreview] = useTablePreview();
  const md = useBreakpoint("md");
  const lg = useBreakpoint("lg");
  const xl2 = useBreakpoint("2xl");
  const showTable = isRoot || preview === 0 || (md && preview !== 2);
  const showChild = !isRoot && preview !== 0;
  const nested = useNested();
  const ref = useTableCurrentItemRef();
  const { disableUpDownOnFullScreen, ...options } = keyboardOptions ?? {};
  const keyboardHandlers = useTableKeyboardHandlers(table, ref, {
    ...options,
    disableUpDown:
      options?.disableUpDown ??
      (disableUpDownOnFullScreen ? showChild && !showTable : undefined),
  });
  const tableRef = React.useRef<HTMLDivElement | null>(null);
  const childRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!showTable && showChild) childRef?.current?.focus();
    else if (!showChild && showTable) tableRef?.current?.focus();
  }, [showTable, showChild]);

  const [gallery] = useTableGallery();
  const tableCurrent = useTableCurrentItemRef();
  const showTableRef = React.useRef(showTable);
  React.useEffect(() => {
    if (showTable && !showTableRef.current) {
      if (!showChildRef.current) {
        tableRef?.current?.focus();
      }
      setTimeout(
        () =>
          tableCurrent.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          }),
        100,
      );
      setTimeout(
        () =>
          tableCurrent.current?.scrollIntoView({
            behavior: "instant",
            block: "center",
          }),
        300,
      );
    }
    showTableRef.current = showTable;
  }, [tableCurrent, showTable, gallery]);

  const showChildRef = React.useRef(showChild);
  React.useEffect(() => {
    const prevShowTable = showTableRef.current;
    const prevShowChild = showChildRef.current;
    showTableRef.current = showTable;
    showChildRef.current = showChild;

    let t: any = undefined;

    if (showTable && !prevShowTable) {
      if (!showChild) {
        tableRef?.current?.focus();
      }
      t = setTimeout(
        () =>
          tableCurrent.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          }),
        gallery ? 1000 : 0,
      );
    }
    if (showChild && !showTable) {
      childRef?.current?.focus();
    }
    return () => clearTimeout(t);
  }, [tableCurrent, showChild, showTable, gallery]);

  return (
    <div
      className={cn(
        !nested && "-my-6 -mr-8 -mx-4 md:-mx-8 h-[calc(100%+3rem)]",
      )}
    >
      <SideBySide
        showParent={showTable}
        showChild={showChild}
        min={xl2 ? [20, 25] : lg ? [20, 30] : [40, 40]}
        defaultLayout={[60, 40]}
        onCollapse={() => setPreview(0)}
        onExpand={() => setPreview(2)}
      >
        {{
          parent: (
            <div
              data-component="side-by-side-left"
              className={cn(
                "overflow-x-hidden h-full focus:outline-azure/50",
                !nested && "pl-4 md:pl-8 pt-2 h-sm:pt-4 h-lg:pt-6",
                !showChild && !nested && "pr-4 md:pr-8",
              )}
              ref={tableRef}
              tabIndex={0}
              {...(keyboardHandlers as any)}
            >
              {children}
            </div>
          ),
          child: (
            <div
              data-component="side-by-side-right"
              className={cn(
                "pl-4 relative h-full focus:outline-azure/50",
                !nested && "pr-4 pt-2 h-sm:pt-4 h-lg:pt-6",
                !showTable && (!nested ? "pl-4 md:pl-8" : "pl-0"),
              )}
              ref={childRef}
              tabIndex={0}
              {...(keyboardHandlers as any)}
            >
              <NestedProvider value={true}>
                <Outlet />
              </NestedProvider>
            </div>
          ),
        }}
      </SideBySide>
    </div>
  );
}
